import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { phone as phoneLib } from '@parsina/framework';

import { bffQuery } from '../lib/bffClient';
import { LOOKUP_PATIENTS_BY_PHONE } from '../lib/patient';

const NONE = Object.freeze([]);

/**
 * The records already on a mobile number, for every registration surface.
 *
 * Fed whatever the form holds — E.164 from `PhoneInput`, or a raw value a voice
 * transcript or a prefill wrote — and it only asks once that value PARSES. The kit
 * field emits `''` until the number is complete, so a lookup fires when the number
 * becomes valid, not on every keystroke; the short delay absorbs a paste followed
 * by a correction.
 *
 * Answers are kept per number for the life of the form: returning to a number the
 * desk already looked at asks nothing, and a number whose lookup failed is simply
 * asked again next time. A failed lookup is not a refusal — `addPatient` re-checks
 * the number and answers `PHONE_HAS_PATIENTS` itself.
 *
 * `seed` records candidates that arrived some other way (that refusal), so the
 * list the desk sees is the one the server just used.
 */
export function usePhoneCandidates(raw, { delay = 400 } = {}) {
  const number = raw ? (phoneLib.normalizePhone(String(raw).trim()) || '') : '';
  const cache = useRef(new Map());
  const [, rerender] = useReducer((n) => n + 1, 0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!number || cache.current.has(number)) { setLoading(false); return undefined; }
    let live = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await bffQuery(LOOKUP_PATIENTS_BY_PHONE, { phone: number });
        cache.current.set(number, data?.lookupPatientsByPhone?.candidates || NONE);
      } catch {
        // Not cached, so the next visit to this number asks again.
      } finally {
        if (live) { setLoading(false); rerender(); }
      }
    }, delay);
    return () => { live = false; clearTimeout(timer); };
  }, [number, delay]);

  const seed = useCallback((n, candidates) => {
    const key = phoneLib.normalizePhone(String(n || '').trim()) || '';
    if (!key) return;
    cache.current.set(key, candidates || NONE);
    rerender();
  }, []);

  return {
    number,
    candidates: (number && cache.current.get(number)) || NONE,
    loading: loading && !!number,
    seed,
  };
}

export default usePhoneCandidates;
